import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { Utilities } from '@app/common/utilities';
import { AlertTemplatesComponent } from '@app/components/alert-templates/alert-templates.component';
import { LanguageType, UserLanguageTerm } from '@app/models/user-language-term';
import { BroadcastService } from '@app/services/broadcast.service';
import { CacheService } from '@app/services/cache.service';
import { LanguageTermService } from '@app/services/language-term.service';
import { RestaurantMenuService } from '@app/services/restaurant-menu.service';
import { RestaurantService } from '@app/services/restaurant.service';
import { UserService } from '@app/services/user.service';
import { addHours } from 'date-fns';
import { concat, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { AppTileService } from './app-tile.service';
import { NewsService } from './news.service';
import { UserAppTileService } from './user-app-tile.service';

@Injectable({
  providedIn: 'root'
})
export class UserLanguageTermService {

  constructor(private http: HttpClient,
              private cache: CacheService,
              private httpHelper: HttpHelper,
              private snackBar: MatSnackBar,
              private newsService: NewsService,
              private userAppTileService: UserAppTileService,
              private appTileService: AppTileService,
              private broadcastService: BroadcastService,
              private restaurantService: RestaurantService,
              private restaurantMenuService: RestaurantMenuService,
              private userService: UserService,
              private languageTermService: LanguageTermService) { }

  private readonly cacheKey = 'UserLanguageTermsService-getUserLanguageTerms';
  public useCache = true;
  private getUserLanguageTerms$: Observable<Array<UserLanguageTerm>>;
  private reload$ = new Subject<void>();

  reload() {
    this.useCache && this.cache.removeFromPersistentCache(this.cacheKey);
    this.getUserLanguageTerms$ = null;
    this.reload$.next();

    this.newsService.reloadAllPersonalNews();
    this.newsService.reloadAllTopNews();
    this.userAppTileService.reload();
    this.appTileService.reload();
    this.broadcastService.reload();
    this.restaurantService.reload();
    this.restaurantMenuService.reload();
  }

  getUserLanguageTerms(): Observable<Array<UserLanguageTerm>> {
    if (this.getUserLanguageTerms$) {
      return this.getUserLanguageTerms$;
    } else {
      this.getUserLanguageTerms$ = this.http.get<Array<UserLanguageTerm>>(`${Constants.API_ENDPOINT}/me/userlanguageterms`)
        .pipe(
          map(response => {
            return response.map(p => new UserLanguageTerm(p));
          }),
          tap(data => {
            this.useCache && this.cache.set(this.cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getUserLanguageTerms', result: new Array<UserLanguageTerm>(), rethrow: true })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<UserLanguageTerm>>(this.cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getUserLanguageTerms$);
        }
      }
      return this.getUserLanguageTerms$;
    }
  }

  add(personalization: UserLanguageTerm, doReload = true): Observable<any> {
    return this.http.post<Array<UserLanguageTerm>>(`${Constants.API_ENDPOINT}/me/userlanguageterms`, personalization)
      .pipe(
        tap(response => {
          console.log(`added user language: `);
          console.log(response);
          if (doReload) {
            this.reload();
          }
        }),
        catchError(this.httpHelper.handleError({ operation: 'add', result: {}, rethrow: true })));
  }

  delete(id: string): Observable<any> {
    return this.http.delete(`${Constants.API_ENDPOINT}/me/userlanguageterms/${id}`, {

    })
      .pipe(
        tap(response => {
          console.log(`deleted user language: `);
          console.log(response);
          this.reload();
        }),
        catchError(this.httpHelper.handleError({ operation: 'delete', result: {}, rethrow: true })));
  }

  syncUserLanguageTermsWithAD(currentRouteUrl: string): Observable<any> {
    const languagesToPushFromAD = new Array<UserLanguageTerm>();
    const alreadyPushedCacheKey = `${this.cacheKey}-alreadyPushed`;
    const alreadyPushed = this.cache.get<boolean>(alreadyPushedCacheKey); // use this to make sure we only try to do the push/sync operation once per 24h
    const requestedPath = this.cache.getWithCustomKey<string>(Constants.WANTED_ROUTE_PATH);
    if (currentRouteUrl.endsWith('settings') || requestedPath?.endsWith('settings')) {
      return of(null);
    }

    if (!alreadyPushed) {
      return forkJoin(
        [
          this.languageTermService.getLanguages(true),
          this.userService.getMyDetailsFromMsGraph(true),
          this.getUserLanguageTerms()
        ])
        .pipe(
          mergeMap(([languages, myProperties, userLanguageTerms]) => {

            languages.forEach(language => {

              // check userLanguages (settings in database) to see if this should be selected
              const matchingUserLanguageTerm = userLanguageTerms.find(p => p.LanguageTermId === language.Guid);

              let azureADUserProperty: string;

              const mappingProperty = language.LocalCustomProperties[Constants.SETTINGS_GRAPH_API_PERSONALIZATION_MAPPING_KEY];

              if (mappingProperty && myProperties) {
                const mappingPropertyKey = Object.keys(myProperties).find(key => key.toLowerCase() === mappingProperty.toLowerCase());
                if (mappingProperty) {
                  azureADUserProperty = myProperties[mappingPropertyKey];
                }
              }

              // check for languages pushed from AD to this user
              if (azureADUserProperty) {
                let matchingProperty = language.Name.replace(/＆/g, '&').toLowerCase() === azureADUserProperty.toLowerCase();
                if (!matchingProperty) {
                  matchingProperty = language.Labels.some(l => l.Name.replace(/＆/g, '&').toLowerCase() === azureADUserProperty.toLowerCase());
                }

                if (matchingProperty) {
                  if (!matchingUserLanguageTerm) {
                    if (!languagesToPushFromAD.find(p => p.LanguageTermId === language.Guid) && !userLanguageTerms.find(p => p.LanguageTermId === language.Guid)) {
                      languagesToPushFromAD.push(new UserLanguageTerm({ LanguageTermId: language.Guid, LanguageType: LanguageType.FromAd }));
                    }
                  }
                }
              }
            });

            this.cache.set(alreadyPushedCacheKey, true, addHours(new Date(), 24));

            if (languagesToPushFromAD.length > 0 && !alreadyPushed) {
              const languagesToAdd = languagesToPushFromAD.map(p => this.add(p, false));
              const concatenatedAdds = concat(...languagesToAdd);
              return forkJoin([concatenatedAdds]);
            } else {
              return of(null);
            }
          }),
          tap((result) => {
            if (result) {
              Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, AlertTemplatesComponent.languageselectionupdated);
            }
            this.reload();
          })
        );
    } else {
      return of(null);
    }
  }

}
